import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { listProfileTabData, mapApiTabFilterToUi } from '../../../services/profileTabData.js';
import { mapApiEntityListItemToUi } from '../../../services/entities.js';
import { normalizePagedMeta } from '../../../shared/utils/apiResponse.js';
import { apiErrorStatus } from './apiErrorStatus.js';

const EMPTY_META = Object.freeze({
  totalItems: 0,
  totalPages: 0,
  currentPage: 1,
  perPage: 0,
  hasNext: false,
  hasPrevious: false,
  nextCursor: null,
});

/**
 * Rows and filter definitions for one dynamic tab.
 *
 * Filters arrive WITH the rows rather than from a separate call: which filters
 * a tab offers is part of how that tab is configured, and splitting them would
 * mean two requests that can disagree.
 *
 * Rows reuse the entity-list mapper. A tab row and an entity row are the same
 * normalized card contract, so one mapper and one card serve both.
 */
export function useProfileTabData({
  entityType,
  entityId,
  tabId,
  page = 1,
  pageSize = 10,
  orderBy,
  orderDirection,
  filters,
  search,
} = {}) {
  const [items, setItems] = useState([]);
  const [filterDefs, setFilterDefs] = useState([]);
  const [sortDefs, setSortDefs] = useState([]);
  const [meta, setMeta] = useState(EMPTY_META);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const fetchIdRef = useRef(0);

  // Serialized so a new object literal from the caller does not re-fire the
  // effect on every render — the VALUES are what should drive a refetch.
  const filtersKey = JSON.stringify(filters ?? {});
  const searchKey = JSON.stringify(search ?? null);

  const fetchData = useCallback(async () => {
    if (!entityType || !entityId || !tabId) return;
    const requestId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    setStatus(null);

    try {
      const { body, meta: rawMeta } = await listProfileTabData({
        entityType,
        entityId,
        tabId,
        page,
        pageSize,
        orderBy,
        orderDirection,
        filters: JSON.parse(filtersKey),
        ...(JSON.parse(searchKey) ? { search: JSON.parse(searchKey) } : {}),
      });
      if (requestId !== fetchIdRef.current) return;
      setItems((body?.items ?? []).map(mapApiEntityListItemToUi));
      setFilterDefs((body?.filters ?? []).map(mapApiTabFilterToUi));
      setSortDefs(body?.sorts ?? []);
      setMeta(normalizePagedMeta(rawMeta) ?? EMPTY_META);
    } catch (err) {
      if (requestId !== fetchIdRef.current) return;
      setItems([]);
      setFilterDefs([]);
      setSortDefs([]);
      setMeta(EMPTY_META);
      setStatus(apiErrorStatus(err));
      setError(err?.message ?? 'Failed to load this tab');
    } finally {
      if (requestId === fetchIdRef.current) setLoading(false);
    }
  }, [entityType, entityId, tabId, page, pageSize, orderBy, orderDirection, filtersKey, searchKey]);

  useEffect(() => {
    fetchData();
    return () => {
      fetchIdRef.current += 1;
    };
  }, [fetchData]);

  return useMemo(
    () => ({
      items,
      filterDefs,
      sortDefs,
      meta,
      loading,
      error,
      accessDenied: status === 403,
      notFound: status === 404,
      refetch: fetchData,
    }),
    [items, filterDefs, sortDefs, meta, loading, error, status, fetchData],
  );
}

export default useProfileTabData;
